// 외주 의뢰서 — 보낸 의뢰·받은 의뢰 목록과 상세 (미리보기 v11 L3a·L3b·L5·M7)
// 상태는 저절로 바뀐다: 업체가 열면 「확인」(markOutsourceOpened), STL 을 내려받으면 「제작 중」(getOutsourceFileUrl).
// 누르는 버튼: 업체 [거절](사유 필수) · 업체 [출고](제작 중 → 출고, 10-23) · 보낸 쪽 [취소](제작 중 전까지) · 보낸 기공소 대표 [환자 이름 지우기] · 업체 대표 [명세서 만들기](출고 기준)
import React, { useEffect, useMemo, useState } from 'react';
import {
    MF_KIND_LABEL, MF_STATUS, teethLabel, fileNos, mfMsOf,
    readSenderExtras, readVendorPatientName, markMfOpened, getMfFileUrl, rejectMf, cancelMf, eraseMfPatientName, buildMfStatement, markMfShipped,
    listVendorJigUsage,
} from '../../services/outsourceMfApi';
import { S, KindChip, StatusChip, MfBrokerNotice, MfStepper, MfStatusLog, Modal, fmtMd, fmtMdHm, fmtDue } from './mfUi';

// CAD 디자인(기존 designOutsourceRequests) 행 상태 — OutsourceRequestPage LIST_STATUS 와 같은 뜻의 짧은 말
const CAD_STATUS = {
    requested: '수락 대기', accepted: '수락', designing: '작업 중', revision_requested: '작업 중',
    delivered: '확인해 주세요', reviewed: '확인해 주세요', statement_issued: '완료', completed: '완료', rejected: '거절', cancelled: '취소',
};
const kstMonth = (ms) => new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 7);
const errText = (e, fallback) => (e && e.message && !/^internal$/i.test(e.message) ? e.message : fallback);

function Filters({ chips, value, onChange, right }) {
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            {chips.map((c) => <button key={c.key} type="button" style={S.chipBtn(value === c.key)} onClick={() => onChange(c.key)}>{c.label}{' '}<b>{c.count}</b></button>)}
            <span style={{ flex: 1 }} />
            {right}
        </div>
    );
}

// ══════════════ 보낸 의뢰 ══════════════
export function MfSentList({ orders, onOpen }) {   // 10-30: 옛 외주(이전 외주) 보기 삭제 — 옛 데이터 삭제(대표)
    const [chip, setChip] = useState('all');
    const [kindF, setKindF] = useState('');
    const [vendorF, setVendorF] = useState('');
    const [monthF, setMonthF] = useState('this');
    const nowMonth = kstMonth(Date.now());
    const rows = useMemo(() => {
        const mf = orders.map((o) => ({ type: 'mf', id: o.id, at: mfMsOf(o.createdAt) || 0, kind: o.kind, vendor: o.toName, caseNo: o.caseNo, teeth: teethLabel(o.teeth, o.bridge), status: o.status, due: o.dueDate, o }));
        return mf.sort((a, b) => b.at - a.at);   // 10-30: CAD 디자인 외주 삭제 — CAD 행 없음
    }, [orders]);
    const inMonth = (r) => monthF === 'all' || kstMonth(r.at) === nowMonth;
    const base = rows.filter((r) => inMonth(r) && (!kindF || r.kind === kindF) && (!vendorF || r.vendor === vendorF));
    const groupOf = (r) => (r.type === 'cad' ? (['rejected', 'cancelled'].includes(r.status) ? 'ended' : 'cad') : (['rejected', 'cancelled'].includes(r.status) ? 'ended' : r.status));
    const count = (k) => base.filter((r) => k === 'all' || groupOf(r) === k).length;
    const shown = base.filter((r) => chip === 'all' || groupOf(r) === chip);
    const vendors = [...new Set(rows.map((r) => r.vendor).filter(Boolean))];
    return (
        <div>
            <Filters value={chip} onChange={setChip}
                chips={[{ key: 'all', label: '전체', count: count('all') }, { key: 'pending', label: '보냄', count: count('pending') }, { key: 'opened', label: '확인', count: count('opened') }, { key: 'in_production', label: '제작 중', count: count('in_production') }, { key: 'shipped', label: '출고', count: count('shipped') }, { key: 'ended', label: '거절·취소', count: count('ended') }]}
                right={<>
                    <select style={{ ...S.input, width: 'auto' }} value={kindF} onChange={(e) => setKindF(e.target.value)}><option value="">{'종류 전체'}</option>{Object.keys(MF_KIND_LABEL).map((k) => <option key={k} value={k}>{MF_KIND_LABEL[k]}</option>)}</select>
                    <select style={{ ...S.input, width: 'auto' }} value={vendorF} onChange={(e) => setVendorF(e.target.value)}><option value="">{'업체 전체'}</option>{vendors.map((v) => <option key={v}>{v}</option>)}</select>
                    <select style={{ ...S.input, width: 'auto' }} value={monthF} onChange={(e) => setMonthF(e.target.value)}><option value="this">{'이번 달'}</option><option value="all">{'전체 기간'}</option></select>
                </>} />
            <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                    <thead><tr>{['보낸 날', '종류', '업체', '케이스 번호', '치아', '상태', '납기', ''].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                        {shown.length === 0 && <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>{'보낸 의뢰가 없습니다'}</td></tr>}
                        {shown.map((r) => (
                            <tr key={`${r.type}-${r.id}`} style={{ cursor: 'pointer' }} onClick={() => onOpen(r.o)}>
                                <td style={S.td}>{fmtMd(r.at)}</td>
                                <td style={S.td}><KindChip kind={r.kind} small /></td>
                                <td style={S.td}>{r.vendor}</td>
                                <td style={{ ...S.td, fontWeight: 700 }}>{r.caseNo}</td>
                                <td style={S.td}>{r.teeth && <span style={{ ...S.tag, color: '#4338ca', background: '#eef2ff', border: 'none' }}>{r.teeth}</span>}</td>
                                <td style={S.td}>{r.type === 'mf' ? <StatusChip status={r.status} /> : <span style={{ ...S.tag, color: '#1d4ed8', background: '#dbeafe', border: 'none', fontWeight: 700 }}>{CAD_STATUS[r.status] || r.status}</span>}</td>
                                <td style={S.td}>{fmtDue(r.due)}</td>
                                <td style={{ ...S.td, color: '#4f46e5', fontWeight: 600 }}>{'자세히'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={S.hint}>{'상태는 저절로 바뀝니다 — 업체가 의뢰를 열면 「확인」, STL을 내려받으면 「제작 중」. 누를 버튼이 없습니다.'}</div>
        </div>
    );
}

export function MfSentDetail({ order, me, onBack, onChanged }) {
    const [extras, setExtras] = useState({ patient: null, memo: '' });
    const [busy, setBusy] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [msg, setMsg] = useState('');
    useEffect(() => { let on = true; readSenderExtras(order.id).then((x) => on && setExtras(x)).catch((e) => console.error('[외주 의뢰] 실명·메모 읽기 실패 code=%s', e && e.code)); return () => { on = false; }; }, [order.id]);
    const canCancel = ['pending', 'opened'].includes(order.status);
    const d = order.detail || {};
    const doCancel = async () => {
        setBusy(true); setMsg('');
        try { await cancelMf(order.id, reason, me.uid); setCancelOpen(false); onChanged(); }
        catch (e) { console.error('[외주 의뢰] 취소 실패 code=%s', e && e.code); setMsg(e && e.code === 'permission-denied' ? '취소할 수 없습니다 — 업체가 이미 STL을 내려받았을 수 있습니다. 업체에 직접 연락해 주세요.' : '취소하지 못했습니다'); }
        finally { setBusy(false); }
    };
    const doErase = async () => {
        if (!window.confirm('환자 이름만 지웁니다. 상태·기록은 남고, 우리와 업체 화면 모두 케이스 번호만 보입니다. 지울까요?')) return;
        try { await eraseMfPatientName(order.id); setExtras((x) => ({ ...x, patient: { ...(x.patient || {}), patientName: '' } })); }
        catch (e) { setMsg(errText(e, '환자 이름을 지우지 못했습니다')); }
    };
    const download = async (n) => {
        try { const r = await getMfFileUrl(order.id, n); window.location.href = r.url; }
        catch (e) { setMsg(errText(e, '내려받지 못했습니다')); }
    };
    const name = extras.patient ? (extras.patient.patientName || '(지움)') : '';
    return (
        <div>
            <button type="button" style={{ border: 'none', background: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 10 }} onClick={onBack}>{'‹ 보낸 의뢰'}</button>
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 18 }}>{order.caseNo}</b><StatusChip status={order.status} /><KindChip kind={order.kind} />
                    <span style={{ fontSize: 13, color: '#475569' }}>{order.toName}{' · '}{teethLabel(order.teeth, order.bridge)}</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" style={canCancel ? S.dangerBtn : { ...S.ghostBtn, color: '#cbd5e1', cursor: 'default' }} disabled={!canCancel} onClick={() => setCancelOpen(true)}>{'취소'}</button>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{'취소는 「제작 중」 전까지만 — 그 뒤엔 업체에 직접 연락'}</span>
                </div>
                {['rejected', 'cancelled'].includes(order.status) ? null : <MfStepper order={order} />}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 520px', minWidth: 0 }}>
                    <div style={S.card}>
                        <div style={S.cardTitle}>{'케이스'}</div>
                        <Row k="케이스 번호" v={<b>{order.caseNo}</b>} />
                        <Row k="환자" v={<>{name}{order.kind === 'cnc' && <span style={{ fontSize: 12, color: '#94a3b8' }}>{' CNC 업체에는 케이스 번호만 전달(약관 개정 후 실명)'}</span>}
                            {me.isOwner && extras.patient && extras.patient.patientName && <button type="button" style={{ ...S.ghostBtn, padding: '2px 8px', fontSize: 11.5, marginLeft: 8 }} onClick={doErase}>{'환자 이름 지우기'}</button>}</>} />
                        <Row k="내 메모 (치과)" v={<>{extras.memo || '-'} <span style={{ fontSize: 11, background: '#f1f5f9', borderRadius: 6, padding: '1px 6px' }}>{'업체에 안 보냄'}</span></>} />
                        <Row k="치아" v={teethLabel(order.teeth, order.bridge)} />
                        {order.kind === 'cnc'
                            ? <><Row k="임플란트" v={[d.system, d.connection, d.platform, d.diameter].filter(Boolean).join(' · ')} /><Row k="재료" v={d.material} /><Row k="후가공" v={d.anodizing ? `아노다이징 ${d.anodizingColor || ''}` : ''} /></>
                            : <><Row k="종류" v={[d.item, d.qty ? `수량 ${d.qty}` : ''].filter(Boolean).join(' · ')} /><Row k="소재" v={d.material} />{d.shade && <Row k="쉐이드" v={d.shade} />}{d.postProcess && <Row k="후처리" v={d.postProcess} />}</>}
                        <Row k="납기" v={fmtDue(order.dueDate)} />
                        <Row k="요청 사항" v={order.note} />
                    </div>
                    <div style={S.card}>
                        <div style={S.cardTitle}>{'보낸 파일'}</div>
                        {order.filesFrom === 'agent' && !order.filesReadyAt && !['rejected', 'cancelled'].includes(order.status) && (
                            <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>{'PC에서 STL을 올리는 중입니다 (덴클 프로그램이 켜져 있어야 합니다).'}</div>
                        )}
                        {fileNos(order).map((n) => (
                            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', background: '#eef2ff', borderRadius: 4, padding: '2px 6px' }}>{'STL'}</span>
                                <span style={{ fontSize: 13 }}>{`${order.caseNo}_${n}.stl`}</span><span style={{ flex: 1 }} />
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>{order.filesDeletedAt ? `삭제됨 ${fmtMdHm(order.filesDeletedAt)}` : ''}</span>
                                {!order.filesDeletedAt && !['rejected', 'cancelled'].includes(order.status) && <button type="button" style={{ ...S.ghostBtn, padding: '3px 10px', fontSize: 12 }} onClick={() => download(n)}>{'받기'}</button>}
                            </div>
                        ))}
                        <div style={S.hint}>{'업체가 내려받은 뒤 24시간 후 자동 삭제됩니다(내려받지 않아도 최대 7일).'}</div>
                    </div>
                </div>
                <div style={{ flex: '1 1 300px', minWidth: 260 }}>
                    <div style={S.card}><div style={S.cardTitle}>{'상태 기록'}</div><MfStatusLog order={order} viewer="sender" /></div>
                    <div style={S.card}><div style={S.cardTitle}>{'대금'}</div><div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>{'명세서는 업체가 월말에 발행합니다. 「제작 중」이 된 건(내려받은 날 기준 월)이 자동으로 들어갑니다. 발행되면 「받은 명세서」에 보이고, 대금은 업체 계좌로 직접 보냅니다.'}</div></div>
                </div>
            </div>
            {msg && <div style={{ ...S.warn, color: '#dc2626', fontSize: 13 }}>{msg}</div>}
            <MfBrokerNotice kind={order.kind} />
            {cancelOpen && (
                <Modal title={`${order.caseNo} 의뢰를 취소할까요?`} onClose={() => !busy && setCancelOpen(false)}
                    footer={<><button type="button" style={S.ghostBtn} disabled={busy} onClick={() => setCancelOpen(false)}>{'돌아가기'}</button><button type="button" style={{ ...S.priBtn, background: '#dc2626' }} disabled={busy} onClick={doCancel}>{'취소하기'}</button></>}>
                    <label style={S.label}>{'취소 사유 (선택)'}</label>
                    <input style={S.input} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="예: 디자인 수정" />
                    <div style={S.warn}>{'ⓘ 사유에 환자 이름을 적지 마세요'}</div>
                    <div style={S.hint}>{'취소하면 올린 STL은 바로 삭제됩니다.'}</div>
                </Modal>
            )}
        </div>
    );
}

function Row({ k, v }) {
    if (v == null || v === '') return null;
    return <div style={{ display: 'flex', gap: 12, padding: '5px 0', fontSize: 13.5 }}><span style={{ width: 110, color: '#64748b', flexShrink: 0 }}>{k}</span><span style={{ color: '#0f172a' }}>{v}</span></div>;
}

// ══════════════ 받은 의뢰 ══════════════
// 외주 지그 제작 수 — 우리(제조사)가 받은 CNC·메탈 의뢰에 붙어 완성된 지그(커스텀 치아 수). 대표 10-30: 외주 건은 제조사 제작으로 센다(청구는 아직)
function VendorJigCard({ vendorId, orders }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        if (!vendorId) return;
        listVendorJigUsage(vendorId).then(setRows).catch((e) => { console.error('[외주 지그] 집계 읽기 실패 code=%s', e && e.code); setRows([]); });
    }, [vendorId]);
    if (!rows || !rows.length) return null;
    const cur = rows.find((r) => r.month === kstMonth(Date.now())) || null;
    const nameOf = (labId) => (orders.find((o) => o.fromUserId === labId) || {}).fromName || '기공소';
    const byLab = Object.entries((cur && cur.byLab) || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
    const past = rows.filter((r) => r !== cur).slice(0, 3);
    return (
        <div style={{ ...S.card, marginBottom: 12, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{'이번 달 지그 제작'}</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{(cur && cur.count) || 0}{'개'}</div>
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 0, fontSize: 12.5, color: '#334155' }}>
                {byLab.length ? byLab.map(([id, n]) => <span key={id} style={{ ...S.tag, marginRight: 6 }}>{`${nameOf(id)} ${n}개`}</span>) : <span style={{ color: '#94a3b8' }}>{'이번 달은 아직 없습니다'}</span>}
                {past.length > 0 && <div style={{ marginTop: 6, color: '#94a3b8' }}>{past.map((r) => `${r.month} ${r.count || 0}개`).join(' · ')}</div>}
            </div>
            <div style={{ ...S.hint, flexBasis: '100%' }}>{'기공소가 보낸 CNC·메탈 커스텀 의뢰에 붙은 지그를 우리 업체 제작분으로 셉니다. 완성된 것만, 커스텀 치아 1개당 1개.'}</div>
        </div>
    );
}

// 받은 의뢰 표 종류 칸 — CNC 는 임플란트 시스템(+커넥션·플랫폼). 시스템이 없으면 null(종류 칩으로)
function implantOf(o) {
    const d = o.detail || {};
    if (o.kind !== 'cnc' || !d.system) return null;
    return { system: d.system, sub: [d.connection, d.platform].filter(Boolean).join(' · ') };
}

export function MfReceivedList({ orders, vendorProfile, me, onChanged }) {
    const [chip, setChip] = useState('all');
    const [labF, setLabF] = useState('');
    const [openId, setOpenId] = useState(null);
    const labs = [...new Set(orders.map((o) => o.fromName).filter(Boolean))];
    const base = orders.filter((o) => !labF || o.fromName === labF);
    const grp = (o) => (['rejected', 'cancelled'].includes(o.status) ? 'ended' : o.status);
    const count = (k) => base.filter((o) => k === 'all' || grp(o) === k).length;
    const shown = base.filter((o) => chip === 'all' || grp(o) === chip);
    const open = orders.find((o) => o.id === openId) || null;
    const kinds = (vendorProfile?.kinds || []).filter((k) => k !== 'cad');
    // 10-31 대표: 케이스 번호 대신 환자 이름 — 한 건씩 읽는다(rules: list 금지·기한/CNC 차단이면 null → 케이스 번호로 대신)
    const [names, setNames] = useState({});
    const idsKey = orders.map((o) => o.id).join(',');
    useEffect(() => {
        let on = true;
        const todo = orders.filter((o) => !(o.id in names));
        if (!todo.length) return undefined;
        Promise.all(todo.map((o) => readVendorPatientName(o.id).then((n) => [o.id, n]).catch(() => [o.id, null])))
            .then((pairs) => { if (on) setNames((m) => ({ ...m, ...Object.fromEntries(pairs) })); });
        return () => { on = false; };
    }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div>
            <VendorJigCard vendorId={me && me.companyId} orders={orders} />
            <div style={{ ...S.info, background: '#eef2ff', borderColor: '#c7d2fe' }}>{'ⓘ 「받은 의뢰」는 외주 수주가 승인된 기공소와 CNC 제조업체에만 보입니다. 우리 기공소 받는 종류: '}{kinds.map((k) => <span key={k} style={{ marginRight: 4 }}><KindChip kind={k} small /></span>)}</div>
            <Filters value={chip} onChange={setChip}
                chips={[{ key: 'all', label: '전체', count: count('all') }, { key: 'pending', label: '새 의뢰', count: count('pending') }, { key: 'opened', label: '확인', count: count('opened') }, { key: 'in_production', label: '제작 중', count: count('in_production') }, { key: 'shipped', label: '출고', count: count('shipped') }, { key: 'ended', label: '거절·취소', count: count('ended') }]}
                right={<select style={{ ...S.input, width: 'auto' }} value={labF} onChange={(e) => setLabF(e.target.value)}><option value="">{'기공소 전체'}</option>{labs.map((l) => <option key={l}>{l}</option>)}</select>} />
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ ...S.card, padding: 0, flex: '1 1 620px', minWidth: 0, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead><tr>{['받은 날', '종류', '보낸 기공소', '환자', '치아', '종류 · 수량 / 소재', '납기', '상태'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                        <tbody>
                            {shown.length === 0 && <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>{'받은 의뢰가 없습니다'}</td></tr>}
                            {shown.map((o) => (
                                <tr key={o.id} style={{ cursor: 'pointer', background: openId === o.id ? '#f8fafc' : undefined }} onClick={() => setOpenId(o.id)}>
                                    <td style={S.td}>{fmtMd(o.createdAt)}</td>
                                    <td style={S.td}>{implantOf(o) ? <><div style={{ fontWeight: 700 }}>{implantOf(o).system}</div>{implantOf(o).sub && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{implantOf(o).sub}</div>}</> : <KindChip kind={o.kind} small />}</td>
                                    <td style={S.td}>{o.fromName}</td>
                                    <td style={{ ...S.td, fontWeight: 700 }}>{names[o.id] && names[o.id] !== o.caseNo ? <>{names[o.id]}<div style={{ fontSize: 11.5, fontWeight: 400, color: '#94a3b8' }}>{o.caseNo}</div></> : <span style={{ color: '#64748b' }}>{o.caseNo}</span>}</td>
                                    <td style={S.td}><span style={{ ...S.tag, color: '#4338ca', background: '#eef2ff', border: 'none' }}>{teethLabel(o.teeth, o.bridge)}</span></td>
                                    <td style={S.td}>{[(o.detail || {}).item, (o.detail || {}).qty ? `수량 ${o.detail.qty}` : ''].filter(Boolean).join(' · ')}<div style={{ fontSize: 11.5, color: '#94a3b8' }}>{(o.detail || {}).material}</div></td>
                                    <td style={S.td}>{fmtDue(o.dueDate)}</td>
                                    <td style={S.td}><StatusChip status={o.status} received /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ ...S.hint, padding: '0 12px 12px' }}>{'상태는 저절로 바뀝니다 — 의뢰를 열면 「확인」, STL을 내려받으면 「제작 중」. 못 받는 의뢰만 [거절](사유 필수 — 일정 불가·범위 밖·파일 문제·기타, 보낸 기공소에 보임). 거절은 출고 전까지(「제작 중」에서도) 할 수 있습니다.'}</div>
                </div>
                {open && <MfReceivedPanel key={open.id} order={open} me={me} onClose={() => setOpenId(null)} onChanged={onChanged} />}
            </div>
            <MfBrokerNotice kind={open ? open.kind : ''} />
        </div>
    );
}

function MfReceivedPanel({ order, me, onClose, onChanged }) {
    const [name, setName] = useState(undefined);
    const [cur, setCur] = useState(order);
    const [msg, setMsg] = useState('');
    const [rejOpen, setRejOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        let on = true;
        (async () => {
            // 열면 「확인」(받는 회사 사람만 서버가 바꾼다) — 실패해도 화면은 연다(기록만)
            if (order.status === 'pending') {
                try { const r = await markMfOpened(order.id); if (on && r && r.changed) { setCur((c) => ({ ...c, status: 'opened', openedAt: new Date() })); onChanged(true); } }
                catch (e) { console.error('[외주 의뢰] 확인 표시 실패 code=%s', e && e.code); }
            }
            try { const n = await readVendorPatientName(order.id); if (on) setName(n); }
            catch (e) { console.error('[외주 의뢰] 환자 이름 읽기 실패 code=%s', e && e.code); if (on) setName(null); }
        })();
        return () => { on = false; };
    }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const d = cur.detail || {};
    const ended = ['rejected', 'cancelled'].includes(cur.status);
    const download = async (n) => {
        setMsg('');
        try {
            const r = await getMfFileUrl(cur.id, n);
            if (r.first) { setCur((c) => ({ ...c, status: 'in_production', firstDownloadedAt: new Date() })); onChanged(true); }
            window.location.href = r.url;
        } catch (e) {
            const code = e && e.details && e.details.code;
            setMsg(code === 'files-deleted' ? '파일이 이미 삭제되었습니다. 보낸 기공소에 다시 보내 달라고 연락해 주세요.' : code === 'file-missing' ? '보낸 기공소가 아직 이 파일을 올리지 못했습니다.' : code === 'order-ended' ? '거절·취소된 의뢰입니다.' : errText(e, '내려받지 못했습니다'));
        }
    };
    const doReject = async () => {
        setBusy(true); setMsg('');
        try { await rejectMf(cur.id, reason, me.uid); setRejOpen(false); onChanged(); }
        catch (e) { console.error('[외주 의뢰] 거절 실패 code=%s', e && e.code); setMsg(e && e.code === 'reason-required' ? '거절 사유를 적어 주세요' : '거절하지 못했습니다 — 이미 출고했거나 취소된 의뢰는 거절할 수 없습니다'); }
        finally { setBusy(false); }
    };
    const [tracking, setTracking] = useState('');
    const doShip = async () => {
        if (!window.confirm(`${cur.caseNo} 출고 처리할까요?\n출고된 건은 그 달 거래명세서에 들어갑니다.`)) return;
        setBusy(true); setMsg('');
        try {
            await markMfShipped(cur.id, tracking);
            setCur((c) => ({ ...c, status: 'shipped', shippedAt: new Date(), trackingNo: tracking || c.trackingNo }));
            onChanged(true);
        } catch (e) {
            console.error('[외주 의뢰] 출고 실패 code=%s', e && e.code);
            setMsg(e && e.details && e.details.code === 'not-in-production' ? '제작 중인 의뢰만 출고할 수 있습니다' : errText(e, '출고하지 못했습니다'));
        } finally { setBusy(false); }
    };
    const makeStatement = async () => {
        // 10-23: 명세서 기준 = 출고한 달(shippedAt)
        const month = kstMonth(mfMsOf(cur.shippedAt) || Date.now());
        if (!window.confirm(`${cur.fromName}의 ${month} 출고 건을 묶어 거래명세서 초안을 만들까요?`)) return;
        try { const r = await buildMfStatement(cur.fromUserId, month); window.alert(`명세서 초안을 만들었습니다 (${r.count}건${r.needsPriceCheck ? ` · 단가 확인 ${r.needsPriceCheck}건` : ''}). 왼쪽 「외주 거래명세서」에서 단가 확인 후 발행해 주세요.`); }
        catch (e) { setMsg(e && e.details && e.details.code === 'exists' ? `${month} 명세서가 이미 있습니다. 왼쪽 「외주 거래명세서」에서 열어 주세요.` : errText(e, '명세서를 만들지 못했습니다')); }
    };
    const vendorUntil = mfMsOf(cur.vendorNameUntil);
    return (
        <div style={{ ...S.card, flex: '0 1 380px', minWidth: 300 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><b style={{ fontSize: 16 }}>{cur.caseNo}</b><KindChip kind={cur.kind} small /><StatusChip status={cur.status} received /><span style={{ flex: 1 }} /><button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }} onClick={onClose}>{'✕'}</button></div>
            <div style={{ fontSize: 12.5, color: '#64748b', margin: '2px 0 10px' }}>{cur.fromName}{' · '}{fmtMd(cur.createdAt)}{' 보냄'}{cur.firstDownloadedAt ? ` · ${fmtMd(cur.firstDownloadedAt)} 내려받음` : ''}</div>
            <Row k="환자" v={name === undefined ? '…' : name ? <>{name}{vendorUntil ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{` ${fmtMd(vendorUntil)}에 가려짐(제작 중 후 30일)`}</span> : null}</> : <span style={{ color: '#64748b' }}>{cur.kind === 'cnc' ? '케이스 번호로 확인(약관 개정 후 실명)' : '케이스 번호만 표시'}</span>} />
            <Row k="치아" v={teethLabel(cur.teeth, cur.bridge)} />
            <Row k="종류" v={cur.kind === 'cnc' ? [d.system, d.connection, d.platform, d.diameter].filter(Boolean).join(' · ') : [d.item, d.qty ? `수량 ${d.qty}` : ''].filter(Boolean).join(' · ')} />
            <Row k="소재" v={d.material} />
            {d.shade && <Row k="쉐이드" v={d.shade} />}
            {(d.postProcess || d.anodizing) && <Row k="후처리" v={d.postProcess || `아노다이징 ${d.anodizingColor || ''}`} />}
            <Row k="납기" v={fmtDue(cur.dueDate)} />
            <Row k="요청 사항" v={cur.note} />
            <div style={{ marginTop: 8 }}>
                {/* 외주 의뢰 페이지(10-30): PC 에이전트가 STL 을 올리는 건 — 다 올라오기 전엔 받기를 막는다(서버도 files-not-ready 로 거절) */}
                {cur.filesFrom === 'agent' && !cur.filesReadyAt && !ended && (
                    <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>{'기공소 PC에서 파일을 올리는 중입니다. 다 올라오면 알림이 갑니다.'}</div>
                )}
                {(cur.filesFrom !== 'agent' || cur.filesReadyAt) && fileNos(cur).map((n) => (
                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', background: '#eef2ff', borderRadius: 4, padding: '2px 6px' }}>{'STL'}</span>
                        <span style={{ fontSize: 12.5 }}>{`${cur.caseNo}_${n}.stl`}</span><span style={{ flex: 1 }} />
                        {cur.filesDeletedAt || ended
                            ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{'삭제됨'}{cur.filesDeletedAt ? ` ${fmtMd(cur.filesDeletedAt)}` : ''}</span>
                            : <button type="button" style={{ ...S.priBtn, padding: '4px 10px', fontSize: 12 }} onClick={() => download(n)}>{'⬇ 내려받기'}</button>}
                    </div>
                ))}
            </div>
            <div style={{ ...S.info, marginTop: 6 }}>{'📁 받는 파일은 STL뿐입니다. STL 파일은 거래명세서 수집에 잡히지 않습니다. 처음 내려받으면 「제작 중」이 되고, 24시간 뒤 자동 삭제됩니다.'}</div>
            {['pending', 'opened'].includes(cur.status) && <button type="button" style={{ ...S.dangerBtn, width: '100%' }} onClick={() => setRejOpen(true)}>{'거절'}</button>}   {/* 제작 중이면 아래 출고 칸에 [거절]이 같이 있다(10-31) */}
            {/* 출고 — 업체 대표·직원. 제작 중에서만 (10-23) */}
            {cur.status === 'in_production' && (
                <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{'출고'}</div>
                    <input style={{ ...S.input, marginTop: 6 }} value={tracking} maxLength={40} placeholder="택배 송장번호 (선택)" onChange={(e) => setTracking(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" style={{ ...S.priBtn, flex: 2 }} disabled={busy} onClick={doShip}>{'🚚 출고 처리'}</button>
                        <button type="button" style={{ ...S.dangerBtn, flex: 1 }} disabled={busy} onClick={() => setRejOpen(true)}>{'거절'}</button>
                    </div>
                    <div style={S.hint}>{'출고하면 보낸 기공소에 알림이 가고 거래명세서에 넣을 수 있습니다. 거절하면 명세서에서 빠집니다(사유는 보낸 기공소에 보임).'}</div>
                </div>
            )}
            {cur.status === 'shipped' && me.isOwner && (
                <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{'거래명세서'}</div>
                    <div style={{ fontSize: 12.5, color: '#64748b', margin: '4px 0 8px' }}>{'주문 기록(케이스·치아·종류·수량)과 단가표로 자동 작성합니다. 환자 이름도 같이 들어갑니다. 「외주 거래명세서」 메뉴에 들어갑니다.'}</div>
                    <button type="button" style={{ ...S.priBtn, width: '100%' }} onClick={makeStatement}>{'📄 명세서 만들기'}</button>
                    <div style={S.hint}>{'같은 기공소의 그 달 출고 건(출고한 날 기준)을 묶어 한 장으로 만듭니다.'}</div>
                </div>
            )}
            <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 10 }}><div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{'기록'}</div><MfStatusLog order={cur} viewer="vendor" /></div>
            {msg && <div style={{ ...S.warn, color: '#dc2626', fontSize: 13 }}>{msg}</div>}
            {rejOpen && (
                <Modal title={`${cur.caseNo} 의뢰를 거절할까요?`} onClose={() => !busy && setRejOpen(false)}
                    footer={<><button type="button" style={S.ghostBtn} disabled={busy} onClick={() => setRejOpen(false)}>{'돌아가기'}</button><button type="button" style={{ ...S.priBtn, background: '#dc2626' }} disabled={busy || !reason.trim()} onClick={doReject}>{'거절'}</button></>}>
                    <label style={S.label}>{'거절 사유 (필수 · 보낸 기공소에 보임)'}</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>{['일정 불가', '범위 밖', '파일 문제', '기타'].map((x) => <button key={x} type="button" style={S.chipBtn(reason === x)} onClick={() => setReason(x)}>{x}</button>)}</div>
                    <input style={S.input} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="직접 적기" />
                    <div style={S.warn}>{'ⓘ 사유에 환자 이름을 적지 마세요'}</div>
                </Modal>
            )}
        </div>
    );
}

export { CAD_STATUS, kstMonth, MF_STATUS };
